/**
 * Free Score Landing Page — dedicated ads / paid-traffic entry point.
 *
 * Route: /free-score
 *
 * Design goal: single, obvious conversion action (submit LinkedIn URL for a
 * free score). Everything below the hero is proof material designed to keep
 * the visitor scrolling / re-scrolling to the calculator.
 *
 * Sections:
 *   1. Score calculator hero (existing ScoreCalculator with instanceId="ads-hero")
 *   2. Trust strip (no card required, ~10s, GDPR-compliant)
 *   3. Score dashboard preview (dummy) — shows what the paid audit unlocks
 *   4. Before / after transformation (reused BeforeAfterSection)
 *   5. Use-case cards — 4 personas (Founders, Consultants, Job-seekers, Execs)
 *   6. Testimonials strip (soft social proof)
 *   7. Final CTA — anchor back to the calculator
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ShieldCheck,
  Timer,
  Sparkles,
  TrendingUp,
  Briefcase,
  Rocket,
  Award,
  CheckCircle2,
  Star,
  Search,
  Zap,
} from 'lucide-react';
import { AppShell } from '../components/Layout';
import SeoHead from '../components/seo/SeoHead';
import { FreeScoreHero } from '../components/landing/FreeScoreHero';
import { BeforeAfterSection } from '../components/landing/BeforeAfterSection';
import FreeScorePopup from '../components/landing/FreeScorePopup';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  fadeUp,
  displaySection,
  kicker,
  sectionWrap,
  sectionInner,
} from '../components/landing/shared';

// ---------- Trust strip ------------------------------------------------------
const TrustStrip = () => (
  <section className="border-t border-border/60 bg-secondary/30" aria-label="Why trust this score">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-center">
      {[
        { icon: Timer, label: '~10 seconds' },
        { icon: ShieldCheck, label: 'No card required' },
        { icon: Sparkles, label: 'AI-audited by Blayzrr' },
        { icon: Award, label: 'GDPR & UK PECR compliant' },
      ].map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground">
          <Icon size={15} className="text-primary" aria-hidden="true" />
          <span>{label}</span>
        </div>
      ))}
    </div>
  </section>
);

// ---------- Dashboard preview (dummy) ----------------------------------------
const CATEGORIES = [
  { name: 'Headline', score: 42, max: 100, color: 'bg-rose-500' },
  { name: 'About / Summary', score: 51, max: 100, color: 'bg-amber-500' },
  { name: 'Experience proof', score: 68, max: 100, color: 'bg-sky-500' },
  { name: 'Content cadence', score: 34, max: 100, color: 'bg-rose-500' },
  { name: 'Positioning clarity', score: 58, max: 100, color: 'bg-amber-500' },
  { name: 'Voice consistency', score: 47, max: 100, color: 'bg-amber-500' },
];

const FIX_LIST = [
  { title: 'Rewrite headline with proof + outcome', impact: '+12 pts', category: 'Headline' },
  { title: 'Add 3 quantified achievements to About', impact: '+8 pts', category: 'About' },
  { title: 'Ship 1 researched post per week', impact: '+14 pts', category: 'Cadence' },
  { title: 'Align voice across last 5 posts', impact: '+6 pts', category: 'Voice' },
];

const DashboardPreview = () => {
  const overall = 52;
  const potential = 88;

  return (
    <section
      className={`${sectionWrap} bg-gradient-to-b from-[hsl(220_35%_11%)] to-[hsl(220_40%_8%)] text-slate-100`}
      id="dashboard-preview"
      aria-label="Sample audit dashboard"
      data-testid="ads-dashboard-preview"
    >
      <div className={sectionInner}>
        <motion.div {...fadeUp} className="max-w-3xl mb-10 sm:mb-14">
          <p className="text-[11px] font-mono-edge tracking-[0.28em] uppercase text-sky-400">
            Inside the paid audit
          </p>
          <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl text-white`}>
            Your score, unpacked into <span className="text-sky-400">clear fixes.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            After you upgrade, we run your uploaded LinkedIn PDF through the full Profile Intelligence Framework and score every section — with a plain-English fix list ranked by impact.
          </p>
        </motion.div>

        {/* Mock dashboard */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-4 sm:p-6 lg:p-8 shadow-[0_60px_120px_-40px_hsl(220_60%_2%/0.5)]"
        >
          <div className="grid gap-6 lg:grid-cols-[1.05fr_1.4fr]">
            {/* Left: overall score + upgrade arc */}
            <div className="rounded-2xl border border-white/10 bg-[hsl(220_40%_7%)]/60 p-6">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-mono-edge tracking-[0.28em] uppercase text-slate-400">
                  Overall LinkedIn score
                </p>
                <Badge className="bg-rose-500/20 text-rose-300 border border-rose-500/40 uppercase text-[10px] tracking-widest">
                  Weak
                </Badge>
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <span className="font-display text-6xl sm:text-7xl font-semibold tracking-[-0.05em] tabular-nums text-white">
                  {overall}
                </span>
                <span className="text-slate-400 text-lg font-normal">/100</span>
              </div>
              <Progress value={overall} className="mt-4 h-1.5 [&>div]:bg-rose-500" />

              <div className="mt-6 pt-5 border-t border-white/10">
                <p className="text-[10px] font-mono-edge tracking-[0.28em] uppercase text-sky-400">
                  Achievable after audit
                </p>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="font-display text-4xl font-semibold tracking-[-0.05em] tabular-nums text-white">
                    {potential}
                  </span>
                  <span className="text-slate-400 text-sm">/100</span>
                  <span className="text-emerald-400 text-xs font-medium flex items-center gap-1">
                    <TrendingUp size={12} /> +{potential - overall} pts possible
                  </span>
                </div>
                <Progress value={potential} className="mt-3 h-1.5 [&>div]:bg-sky-500" />
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                {[
                  { label: 'Sections', value: '9' },
                  { label: 'Fixes', value: '18' },
                  { label: 'Priority', value: '5' },
                ].map((k) => (
                  <div key={k.label} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <p className="font-display text-2xl font-semibold tabular-nums text-white">
                      {k.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                      {k.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: category breakdown + prioritized fix list */}
            <div className="grid gap-4">
              <div className="rounded-2xl border border-white/10 bg-[hsl(220_40%_7%)]/60 p-5">
                <p className="text-[10px] font-mono-edge tracking-[0.28em] uppercase text-slate-400 mb-4">
                  Category breakdown
                </p>
                <div className="grid gap-3.5">
                  {CATEGORIES.map((c) => (
                    <div key={c.name}>
                      <div className="flex items-center justify-between text-[13px]">
                        <span className="text-slate-200">{c.name}</span>
                        <span className="tabular-nums text-slate-400">
                          <span className="text-white font-medium">{c.score}</span> / {c.max}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                        <div className={`h-full ${c.color} rounded-full`} style={{ width: `${c.score}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[hsl(220_40%_7%)]/60 p-5">
                <p className="text-[10px] font-mono-edge tracking-[0.28em] uppercase text-slate-400 mb-4">
                  Top fixes (ranked by impact)
                </p>
                <ul className="space-y-2">
                  {FIX_LIST.map((f, i) => (
                    <li
                      key={f.title}
                      className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
                    >
                      <span className="text-[10px] font-mono text-slate-500 tabular-nums w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-100 truncate">{f.title}</p>
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                          {f.category}
                        </p>
                      </div>
                      <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px]">
                        {f.impact}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-5 text-center">
            Sample dashboard — actual layout & priorities are personalised to your profile.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ---------- Use case cards ---------------------------------------------------
const USE_CASES = [
  {
    icon: Rocket,
    tag: 'Founders',
    title: 'Turn your profile into your best sales rep',
    bullets: [
      'Investors & customers land on a clear story',
      'Weekly content that actually sounds like you',
      'Pipeline signals from lurkers → conversations',
    ],
    accent: 'from-sky-500/20 to-sky-500/5',
  },
  {
    icon: Briefcase,
    tag: 'Consultants',
    title: 'Position clearly, price higher',
    bullets: [
      'Cut through the sea of generic consultants',
      'Cadence + proof that attracts inbound',
      'Peer benchmarking on Enterprise*',
    ],
    accent: 'from-emerald-500/20 to-emerald-500/5',
  },
  {
    icon: Search,
    tag: 'Job seekers',
    title: 'Get spotted by the right recruiters',
    bullets: [
      'Recruiter-optimised headline + About',
      'Proof over adjectives — quantified impact',
      'Sharp positioning for the role you actually want',
    ],
    accent: 'from-amber-500/20 to-amber-500/5',
  },
  {
    icon: Award,
    tag: 'Executives',
    title: 'Executive presence, quietly engineered',
    bullets: [
      'Thought-leadership without sounding like everyone',
      'Voice-matched posts you can approve in 60s',
      'Trusted signal for boards, LPs, and hires',
    ],
    accent: 'from-violet-500/20 to-violet-500/5',
  },
];

const UseCases = () => (
  <section className={sectionWrap} aria-label="Who this is for" id="use-cases">
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="max-w-3xl mb-12">
        <p className={kicker}>Who it&apos;s for</p>
        <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}>
          Built for people whose LinkedIn <span className="text-primary">actually matters.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
          Pick the profile that sounds most like you — then get your free score. The full makeover is tuned to your goals.
        </p>
      </motion.div>

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {USE_CASES.map(({ icon: Icon, tag, title, bullets, accent }) => (
          <motion.div key={tag} {...fadeUp}>
            <Card
              className="h-full border-border/70 bg-card/70 hover:border-primary/50 transition-colors relative overflow-hidden group"
              data-testid={`ads-usecase-${tag.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div
                aria-hidden="true"
                className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-70 group-hover:opacity-100 transition-opacity`}
              />
              <CardContent className="relative p-6">
                <div className="w-10 h-10 rounded-xl bg-background/80 border border-border/60 flex items-center justify-center">
                  <Icon size={18} className="text-primary" aria-hidden="true" />
                </div>
                <p className="mt-4 text-[10px] font-mono-edge tracking-[0.24em] uppercase text-muted-foreground">
                  {tag}
                </p>
                <h3 className="mt-1.5 font-display font-semibold text-lg leading-snug tracking-[-0.02em] text-foreground">
                  {title}
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-foreground/80">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="mt-0.5 text-primary shrink-0" aria-hidden="true" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Testimonials strip ----------------------------------------------
const TESTIMONIALS = [
  {
    quote:
      'Went from a headline nobody read to inbound from three ideal-fit clients in the first month.',
    who: 'Founder · fintech',
  },
  {
    quote:
      'The fix list was scarily accurate. I ticked 4 items and my views jumped 3x.',
    who: 'Consultant · leadership',
  },
  {
    quote:
      'Finally content that sounds like me, not corporate LinkedIn.',
    who: 'VP Product · SaaS',
  },
];

const Testimonials = () => (
  <section
    className={`${sectionWrap} bg-secondary/30`}
    aria-label="Testimonials"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="max-w-3xl mb-10">
        <p className={kicker}>What people say</p>
        <h2 className={`${displaySection} mt-4 text-3xl sm:text-4xl lg:text-5xl`}>
          Small nudge, <span className="text-primary">outsized shift.</span>
        </h2>
      </motion.div>
      <div className="grid gap-5 md:grid-cols-3">
        {TESTIMONIALS.map((t) => (
          <motion.div key={t.who} {...fadeUp}>
            <Card className="h-full border-border/70 bg-card/80">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 text-amber-400 mb-3" aria-label="Rating">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={13} fill="currentColor" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-[15px] text-foreground/90 leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="mt-4 text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
                  {t.who}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Final CTA (scroll-back to calculator) ---------------------------
const FinalCTAAds = () => {
  const back = () =>
    document.getElementById('ads-hero-calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <section
      className={`${sectionWrap} bg-gradient-to-b from-background to-secondary/40`}
      aria-label="Final call to action"
    >
      <div className={`${sectionInner} text-center max-w-3xl mx-auto`}>
        <motion.div {...fadeUp}>
          <p className={kicker}>Ready when you are</p>
          <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}>
            10 seconds. One number. <span className="text-primary">Zero risk.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Get your free score now — see the one thing to fix first. The full audit stays optional.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button
              onClick={back}
              size="lg"
              className="h-12 px-6 rounded-full text-[15px] shadow-sm"
              data-testid="ads-final-cta-back-to-hero"
            >
              <Zap size={15} className="mr-2" aria-hidden="true" />
              Get my free score
              <ArrowRight size={16} className="ml-1.5" aria-hidden="true" />
            </Button>
          </div>
          <p className="mt-5 text-xs text-muted-foreground">
            No card required · Works for coaches, consultants, founders, executives
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ---------- Page --------------------------------------------------------------
export default function FreeScoreLandingPage() {
  return (
    <AppShell>
      <SeoHead
        title="Blayzrr — Free LinkedIn profile score in 10 seconds"
        description="Get an instant, AI-audited score for your LinkedIn profile. See where you stand today and the #1 thing to fix first. No card required."
        path="/free-score"
      />
      {/* 1. HERO — redesigned dark, split-layout conversion hero with the
             numbered 3-step "copy → paste → score" flow. Replaces the old
             ScoreCalculator embed but reuses the /api/score-preview
             telemetry endpoint under the hood. */}
      <FreeScoreHero />

      {/* 2. Trust strip */}
      <TrustStrip />

      {/* 3. Dashboard preview (dummy) — shows what upgrading unlocks */}
      <DashboardPreview />

      {/* 4. Before / After — reuse the existing section */}
      <BeforeAfterSection />

      {/* 5. Use case cards */}
      <UseCases />

      {/* 6. Testimonials */}
      <Testimonials />

      {/* 7. Final CTA — anchor back to hero calculator */}
      <FinalCTAAds />

      {/* Dwell + exit-intent popup — fires 5s in OR when user mouses to
          leave the viewport. Once dismissed / submitted, silent for the
          rest of the tab session. Feeds the /free-giveaway pipeline. */}
      <FreeScorePopup />
    </AppShell>
  );
}
